import {
  fieldKey,
  useControlled,
} from "@datapay-by-datacom/design-system";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useAuthenticated } from "../../authn";
import { useUserOrgs } from "../../org/hooks";
import { AgentInvocationRequestSchema } from "../types";
import { useShadowRunAgentId } from "./useShadowRunAgentId";
import { useAgent } from "./useAgent";
import { useAgentServiceAccounts } from "./useAgentServiceAccounts";
import { useOrgDocuments, extractDirectoryPrefixes } from "./useOrgDocuments";
import {
  type TriggerRunFormData,
  INITIAL_FORM_DATA,
  buildInputPayload,
} from "./triggerRunFormUtils";
import { shadowRunProgressPath } from "../utils/workflowId";
import { useTriggerShadowRun } from "./useTriggerShadowRun";

export type { TriggerRunFormData } from "./triggerRunFormUtils";
export { buildInputPayload } from "./triggerRunFormUtils";

export function useTriggerRunForm() {
  const auth = useAuthenticated();
  const navigate = useNavigate();

  const userId = auth.state === "authenticated" ? auth.userId : "";
  const userOrgsQuery = useUserOrgs(userId);

  const { agentId: configuredAgentId, isConfigured: isAgentConfigured, isLoading: isAgentConfigLoading } =
    useShadowRunAgentId();
  const effectiveAgentId = configuredAgentId ?? "";

  const serviceAccountsQuery = useAgentServiceAccounts(effectiveAgentId);
  const agentQuery = useAgent(effectiveAgentId);

  const [selectedServiceAccountId, setSelectedServiceAccountId] = useState<
    string | null
  >(null);

  const serviceAccounts = useMemo(
    () => (serviceAccountsQuery.data ?? []).filter((sa) => sa.is_active),
    [serviceAccountsQuery.data],
  );

  // Auto-select first SA when they load
  useEffect(() => {
    if (serviceAccounts.length > 0 && !selectedServiceAccountId) {
      setSelectedServiceAccountId(serviceAccounts[0].id);
    }
  }, [serviceAccounts, selectedServiceAccountId]);

  const serviceAccountId = selectedServiceAccountId;

  const [selectedOrgKey, setSelectedOrgKey] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const orgs = useMemo(
    () => (userOrgsQuery.data ?? []).filter((o) => o.category === "internal"),
    [userOrgsQuery.data],
  );
  const defaultKey = orgs.length
    ? `${orgs[0].category}:${orgs[0].id}`
    : null;
  const effectiveKey = selectedOrgKey ?? defaultKey;

  const selectedOrg = orgs.find(
    (o) => `${o.category}:${o.id}` === effectiveKey,
  );

  const documentsQuery = useOrgDocuments(
    selectedOrg?.category,
    selectedOrg?.id,
  );

  const directoryPrefixes = useMemo(() => {
    const docs = documentsQuery.data ?? [];
    return extractDirectoryPrefixes(docs.map((d) => d.doc_source));
  }, [documentsQuery.data]);

  const field = useControlled<TriggerRunFormData>(INITIAL_FORM_DATA);
  const input = fieldKey(field);

  const handleOrgChange = (key: string) => {
    setSelectedOrgKey(key);
    field.onChange({ ...field.value, directoryPath: "" });
  };

  const mutation = useTriggerShadowRun(effectiveAgentId);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (!selectedOrg || !serviceAccountId || !effectiveAgentId) return;

    const inputPayload = buildInputPayload(
      field.value,
      selectedOrg.category,
      selectedOrg.id,
    );

    const invocation = AgentInvocationRequestSchema.safeParse({
      service_account_id: serviceAccountId,
      input_payload: inputPayload,
    });
    if (!invocation.success) {
      const message = invocation.error.issues
        .map((issue) => `${issue.path.join(".")}: ${issue.message}`)
        .join("; ");
      setValidationError(message);
      return;
    }

    setValidationError(null);
    mutation.mutate(invocation.data, {
      onSuccess: (response) => {
        navigate(shadowRunProgressPath(effectiveAgentId, response.workflow_id));
      },
    });
  };

  return {
    field,
    input,
    orgs,
    selectedOrgKey: effectiveKey,
    setSelectedOrgKey: handleOrgChange,
    selectedOrg,
    isAgentConfigured,
    configuredAgentMissing:
      isAgentConfigured &&
      !agentQuery.isLoading &&
      (agentQuery.isError || !agentQuery.data),
    isAgentConfigLoading,
    selectedAgentId: effectiveAgentId,
    serviceAccounts,
    serviceAccountId,
    selectedServiceAccountId,
    setSelectedServiceAccountId,
    agentDetail: agentQuery.data ?? null,
    isAgentLoading: agentQuery.isLoading,
    directoryPrefixes,
    isDocumentsLoading: documentsQuery.isLoading,
    isDocumentsError: documentsQuery.isError,
    documentsError: documentsQuery.error,
    validationError,
    handleSubmit,
    mutation,
  };
}
